import { create } from "zustand";
import { ColumnSortFilterConfig } from "../../modules/core/data/ListData";
import { FilterState, SortState } from "./DataTable";
import { getSortOrderLabel, SORT_ORDER } from "./TableSort";
import {
    CmoQueryKeys,
    DeploymentQueryKeys,
    MigrateOpsQueryKeys,
    ProjectQueryKeys,
    ProjectSettingsQueryKeys,
    ReportQueryKeys,
    UnstructuredDataQueryKeys,
} from "../QueryKeys";
import { getProjectActivitiesFilterConfig, getProjectListFilterConfig } from "../../modules/project/ProjectCommon";
import { getCmoMigrationSessionsListSortFilterConfig } from "../../modules/cmoMigration/CmoMigrationCommon";
import { getReportListSortFilterConfig } from "../../modules/reports/ReportCommon";
import { getDeploymentListFilterConfig } from "../../modules/galaxymigrate/GmDeploymentCommon";
import { getOperationListFilterConfig } from "../../modules/migrateops/MigrateOpsCommon";
import { getListDataSyncSessionsSortFilterConfig } from "../../modules/unstructuredData/components/list/UnstructuredDataListSortFilterConfig";

interface TableState {
    sortState: SortState;
    filters: FilterState[];
    sortFilterConfig: ColumnSortFilterConfig<any, any, any, any>;
}

type TableStateMap = Map<string, TableState>;

interface TableSortFilterState {
    tableStateMap: TableStateMap;
    setSortFilterConfig: (tableKey: string, sortFilterConfig: ColumnSortFilterConfig<any, any, any, any>) => void;
    refreshSortFilterConfig: (tableKey: string) => void;
    setSort: (tableKey: string, config: SortState) => void;
    toggleSortDirection: (tableKey: string) => void;
    clearSort: (tableKey: string) => void;
    setFilter: (tableKey: string, config: FilterState) => void;
    replaceFilters: (tableKey: string, filters: FilterState[]) => void;
    removeFilter: (tableKey: string, i: number) => void;
    clearFilters: (tableKey: string) => void;
    getIsFilterActive: (tableKey: string) => boolean;
}

const getDefaultSortState = (sortFilterConfig: ColumnSortFilterConfig<any, any, any, any>): SortState => {
    return {
        field: sortFilterConfig.sort.defaultSort,
        descending: {
            label: sortFilterConfig.sort.defaultSortDesc ? getSortOrderLabel(SORT_ORDER.DESCENDING) : getSortOrderLabel(SORT_ORDER.ASCENDING),
            value: sortFilterConfig.sort.defaultSortDesc,
        },
    };
};

type SortFilterConfigFactory = () => ColumnSortFilterConfig<any, any, any, any>;

const sortFilterConfigFactories = new Map<string, SortFilterConfigFactory>([
    [ProjectQueryKeys.listMyProjects, getProjectListFilterConfig],
    [CmoQueryKeys.listCmoMigrationSessions, getCmoMigrationSessionsListSortFilterConfig],
    [ReportQueryKeys.listProjectReports, getReportListSortFilterConfig],
    [DeploymentQueryKeys.listGmDeployments, getDeploymentListFilterConfig],
    [MigrateOpsQueryKeys.listProjectOperations, getOperationListFilterConfig],
    [UnstructuredDataQueryKeys.listDataSyncSessions, getListDataSyncSessionsSortFilterConfig],
    [ProjectSettingsQueryKeys.listProjectActivities, getProjectActivitiesFilterConfig],
]);

const getInitialTableStateMap = (): TableStateMap =>
    new Map(
        Array.from(sortFilterConfigFactories, ([tableKey, createSortFilterConfig]) => {
            const sortFilterConfig = createSortFilterConfig();
            return [
                tableKey,
                {
                    sortState: getDefaultSortState(sortFilterConfig),
                    filters: [],
                    sortFilterConfig,
                },
            ];
        })
    );

export const useGlobalTableSortFilterState = create<TableSortFilterState>((set, get) => ({
    tableStateMap: getInitialTableStateMap(),
    setSortFilterConfig: (tableKey, sortFilterConfig) => {
        const tableStateMap = new Map(get().tableStateMap);
        const currentTableState = tableStateMap.get(tableKey);
        const currentSortState = currentTableState?.sortState;
        const sortField =
            sortFilterConfig.sort.sortFields.find((field) => field.fieldId === currentSortState?.field.fieldId) ?? sortFilterConfig.sort.defaultSort;
        const isDescending = currentSortState?.descending.value ?? sortFilterConfig.sort.defaultSortDesc;

        tableStateMap.set(tableKey, {
            ...currentTableState,
            sortFilterConfig,
            sortState: {
                field: sortField,
                descending: {
                    label: getSortOrderLabel(isDescending ? SORT_ORDER.DESCENDING : SORT_ORDER.ASCENDING),
                    value: isDescending,
                },
            },
            filters:
                currentTableState?.filters.map((filter) => ({
                    ...filter,
                    fieldConfig: sortFilterConfig.filter.find((fieldConfig) => fieldConfig.fieldId === filter.fieldConfig.fieldId) ?? filter.fieldConfig,
                })) ?? [],
        });
        set({ tableStateMap });
    },
    refreshSortFilterConfig: (tableKey) => {
        const createSortFilterConfig = sortFilterConfigFactories.get(tableKey);
        if (createSortFilterConfig) {
            get().setSortFilterConfig(tableKey, createSortFilterConfig());
        }
    },
    setSort: (tableKey, sortState) => {
        const tableStateMap = new Map(get().tableStateMap);
        const currentTableState = tableStateMap.get(tableKey);
        tableStateMap.set(tableKey, {
            ...currentTableState,
            sortState,
        });
        set({ tableStateMap });
    },
    toggleSortDirection: (tableKey: string) => {
        const tableStateMap = new Map(get().tableStateMap);
        const currentTableState = tableStateMap.get(tableKey);
        const currentSortState = currentTableState.sortState;
        const isDescending = !currentSortState.descending.value;
        const newSortState = {
            ...currentSortState,
            descending: {
                value: isDescending,
                label: getSortOrderLabel(isDescending ? SORT_ORDER.DESCENDING : SORT_ORDER.ASCENDING),
            },
        };
        tableStateMap.set(tableKey, {
            ...currentTableState,
            sortState: newSortState,
        });
        set({ tableStateMap });
    },
    clearSort: (tableKey: string) => {
        const tableStateMap = new Map(get().tableStateMap);
        const currentTableState = tableStateMap.get(tableKey);
        tableStateMap.set(tableKey, {
            ...currentTableState,
            sortState: getDefaultSortState(currentTableState.sortFilterConfig),
        });
        set({ tableStateMap });
    },
    setFilter: (tableKey: string, filter) => {
        const tableStateMap = new Map(get().tableStateMap);
        const currentTableState = tableStateMap.get(tableKey);
        const currentFilters = currentTableState.filters;
        tableStateMap.set(tableKey, {
            ...currentTableState,
            filters: [...currentFilters, filter],
        });
        set({ tableStateMap });
    },
    replaceFilters: (tableKey: string, filters) => {
        const tableStateMap = new Map(get().tableStateMap);
        const currentTableState = tableStateMap.get(tableKey);
        tableStateMap.set(tableKey, {
            ...currentTableState,
            filters,
        });
        set({ tableStateMap });
    },
    removeFilter: (tableKey, i) => {
        const tableStateMap = new Map(get().tableStateMap);
        const currentTableState = tableStateMap.get(tableKey);
        const currentFilters = currentTableState.filters;
        const newFilters = currentFilters.filter((_, index) => index !== i);
        tableStateMap.set(tableKey, {
            ...currentTableState,
            filters: newFilters,
        });
        set({ tableStateMap });
    },
    clearFilters: (tableKey) => {
        const tableStateMap = new Map(get().tableStateMap);
        const currentTableState = tableStateMap.get(tableKey);
        tableStateMap.set(tableKey, {
            ...currentTableState,
            filters: [],
        });
        set({ tableStateMap });
    },
    getIsFilterActive: (tableKey: string) => {
        const tableStateMap = new Map(get().tableStateMap);
        const currentTableState = tableStateMap.get(tableKey);
        return currentTableState?.filters.length > 0;
    },
}));
